import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, MapPin, Award, Users, Palette } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ImageCarousel from '../components/ImageCarousel';

const HomePage = ({ artworks, galleryInfo, siteSettings }) => {
    const navigate = useNavigate();
    const [animateFeatures, setAnimateFeatures] = useState(false);
    const featuresRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setAnimateFeatures(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.1 }
        );

        if (featuresRef.current) {
            observer.observe(featuresRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <div className="bg-gray-50 overflow-x-hidden">
            <style>{`
                html {
                    overflow-y: scroll;
                }

                @keyframes slideInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                .hero-title {
                    animation: slideInUp 0.8s ease-out 0.2s both;
                }

                .hero-subtitle {
                    animation: slideInUp 0.8s ease-out 0.4s both;
                }

                .hero-button {
                    animation: slideInUp 0.8s ease-out 0.6s both;
                }

                .feature-card {
                    animation: ${animateFeatures ? 'slideInUp 0.8s ease-out forwards' : 'none'};
                }

                .feature-card:nth-child(1) { animation-delay: ${animateFeatures ? '0.1s' : '0s'}; }
                .feature-card:nth-child(2) { animation-delay: ${animateFeatures ? '0.2s' : '0s'}; }
                .feature-card:nth-child(3) { animation-delay: ${animateFeatures ? '0.3s' : '0s'}; }
                .feature-card:nth-child(4) { animation-delay: ${animateFeatures ? '0.4s' : '0s'}; }

                .feature-image {
                    animation: ${animateFeatures ? 'slideInRight 0.8s ease-out 0.2s forwards' : 'none'};
                }
            `}</style>

            <main>
                {/* Hero Section */}
                <div className="relative h-[60vh] sm:h-[65vh] md:h-[75vh] lg:h-[85vh] bg-cover bg-center" style={{ backgroundImage: "url('https://sgkolinskyweb.blob.core.windows.net/$web/hero-home.jpg')" }}>
                    <div className="absolute inset-0 bg-black/60 flex flex-col justify-center items-center text-white p-4 sm:p-6">
                        <h1 className="hero-title text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-serif font-extrabold text-center mb-4 sm:mb-6">Art that Speaks.</h1>
                        <p className="hero-subtitle text-sm sm:text-base md:text-lg lg:text-xl text-center max-w-xl mb-6 sm:mb-10 font-light">Curated collections from Sri Lanka, in Unawatuna, Mirissa & Ahangama.</p>
                        <button onClick={() => navigate('/gallery')} className="hero-button px-6 sm:px-8 lg:px-10 py-2 sm:py-3 lg:py-4 bg-orange-600 text-white text-sm sm:text-base font-semibold rounded-full shadow-xl hover:bg-orange-700 transition duration-300 transform hover:scale-105">Explore Collection</button>
                    </div>
                </div>

                {/* What Makes Us Special Section - FULLY RESPONSIVE */}
                <section ref={featuresRef} className="bg-gradient-to-br from-orange-50 to-white py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-10 sm:mb-12 md:mb-16">
                            <h2 className="hero-title text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">What Makes Us Special</h2>
                            <p className="hero-subtitle text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-2">We showcase exceptional works from local artists, offering authentic Sri Lankan paintings and exceptional pieces that tell the stories of our culture.</p>
                        </div>

                        {/* Responsive Grid - Mobile: 1x4, Tablet: 2x2, Desktop: Image + 2x2 Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
                            {/* Features - Left Side on Desktop, Top on Mobile/Tablet */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 lg:gap-6 order-2 md:order-1">
                                {/* Feature 1 */}
                                <div className="feature-card flex flex-col gap-2 sm:gap-3 p-3 sm:p-4 md:p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition">
                                    <div className="flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-orange-600 text-white flex-shrink-0">
                                        <Palette size={18} />
                                    </div>
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">Curated Collections</h3>
                                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Handpicked paintings and artworks exclusively from talented local Sri Lankan artists.</p>
                                </div>

                                {/* Feature 2 */}
                                <div className="feature-card flex flex-col gap-2 sm:gap-3 p-3 sm:p-4 md:p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition">
                                    <div className="flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-orange-600 text-white flex-shrink-0">
                                        <Award size={18} />
                                    </div>
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">Expert Curation</h3>
                                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Our team brings years of experience in art selection and gallery management.</p>
                                </div>

                                {/* Feature 3 */}
                                <div className="feature-card flex flex-col gap-2 sm:gap-3 p-3 sm:p-4 md:p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition">
                                    <div className="flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-orange-600 text-white flex-shrink-0">
                                        <Users size={18} />
                                    </div>
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">Personal Service</h3>
                                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">We work closely with clients to understand their needs and find the perfect artwork.</p>
                                </div>

                                {/* Feature 4 */}
                                <div className="feature-card flex flex-col gap-2 sm:gap-3 p-3 sm:p-4 md:p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition">
                                    <div className="flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-orange-600 text-white flex-shrink-0">
                                        <MapPin size={18} />
                                    </div>
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">Multiple Locations</h3>
                                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Visit us at Unawatuna, Mirissa, or Ahangama in Sri Lanka's most beautiful areas.</p>
                                </div>
                            </div>

                            {/* Image on Right - Hidden on Mobile, Shows on Tablet and Up */}
                            <div className="feature-image order-1 md:order-2 flex justify-center md:justify-end">
                                <img 
                                    src="https://sgkolinskyweb.blob.core.windows.net/$web/special-gallery.jpg" 
                                    alt="What Makes Us Special" 
                                    className="w-full max-w-sm md:max-w-md lg:max-w-lg h-auto rounded-lg sm:rounded-xl md:rounded-2xl shadow-md sm:shadow-lg transition-shadow hover:shadow-xl object-cover"
                                    onError={(e) => {
                                        e.target.src = 'https://placehold.co/400x500/D4BBAA/333333?text=Gallery+Experience';
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Use ImageCarousel component */}
                <ImageCarousel items={artworks || []} />

                {/* Sanctuary Section */}
                <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-16 items-center">
                            <div className="order-2 md:order-1">
                                <img src="https://sgkolinskyweb.blob.core.windows.net/$web/showcase-home.jpg" alt="Gallery Interior" className="rounded-lg sm:rounded-xl md:rounded-2xl shadow-md sm:shadow-lg md:shadow-xl w-full h-auto object-cover"/>
                            </div>
                            <div className="order-1 md:order-2">
                                <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-gray-800 mb-4 sm:mb-6">A Sanctuary for Creativity</h3>
                                <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 leading-relaxed">Showcasing exceptional local artistry - unique pieces capturing the essence of our tropical home.</p>
                                
                                <div className="space-y-2 sm:space-y-3 mb-6">
                                    <p className="text-sm sm:text-base font-semibold text-gray-700 flex items-start sm:items-center gap-2">
                                        <MapPin size={18} className="text-orange-600 flex-shrink-0 mt-0.5 sm:mt-0" /> 
                                        <span>{galleryInfo.addressUnawatuna}</span>
                                    </p>
                                    <p className="text-sm sm:text-base font-semibold text-gray-700 flex items-start sm:items-center gap-2">
                                        <MapPin size={18} className="text-orange-600 flex-shrink-0 mt-0.5 sm:mt-0" /> 
                                        <span>{galleryInfo.addressMirissa}</span>
                                    </p>
                                    <p className="text-sm sm:text-base font-semibold text-gray-700 flex items-start sm:items-center gap-2">
                                        <MapPin size={18} className="text-orange-600 flex-shrink-0 mt-0.5 sm:mt-0" /> 
                                        <span>Ahangama, Sri Lanka</span>
                                    </p>
                                </div>
                                
                                <button onClick={() => navigate('/contact')} className="text-base sm:text-lg font-medium text-orange-600 hover:text-orange-700 transition flex items-center group">
                                    Find our locations
                                    <ChevronRight size={18} className="ml-1 transition transform group-hover:translate-x-1" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
                
                {/* Transforming Spaces Section */}
                <section className="bg-gray-800 py-12 sm:py-16 md:py-20 lg:py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-3 sm:mb-4 md:mb-6">Transforming Spaces</h3>
                        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto">See our curated pieces in luxury villas, boutique hotels, and private residences.</p>
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                            <button onClick={() => navigate('/contact')} className="px-6 sm:px-8 py-2 sm:py-3 bg-white text-gray-800 text-sm sm:text-base font-semibold rounded-full shadow-lg hover:bg-gray-100 transition duration-300 transform hover:scale-105">
                                Request Consultation
                            </button>
                            <a href={siteSettings.whatsappLink} target="_blank" rel="noopener noreferrer" className="px-6 sm:px-8 py-2 sm:py-3 bg-green-500 text-white text-sm sm:text-base font-semibold rounded-full shadow-lg hover:bg-green-600 transition duration-300 transform hover:scale-105">
                                WhatsApp Us
                            </a>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default HomePage;